/**
 * AnalysisStep — create-wizard step 1 (W12 Part B, Task B2).
 *
 * Props-in / JSX-out: pin the ONE insight analysis, the name suggested from
 * it, and (only when the analysis has more than one usable variable set)
 * which generation to use. Every RULE — the label map, the name-suggestion
 * derivation, the generation auto-pick — lives in `StrategyFormDialog`; this
 * component renders exactly what it is handed, nothing more.
 */
import React from 'react';
import { Stack } from '@mui/material';
import { StyledTextField } from '@modules/ui-components/forms/fields/StyledTextField';
import { StyledSelectDropdown } from '@modules/ui-components/forms/fields/StyledSelectDropdown';
import {
  InsightAnalysisSelect,
  type InsightAnalysisHydration,
  type InsightAnalysisSelection,
} from '@modules/data-pickers';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';

export interface AnalysisStepProps {
  projectId: string;
  /** Currently pinned analysis id (first-class dialog state, not a path). */
  analysisId: string | null;
  onAnalysisSelect: (selection: InsightAnalysisSelection) => void;
  /** Per-analysis accepted insight-variable counts, rendered as option chips. */
  acceptedVariableCounts: Record<string, number>;
  name: string;
  onNameChange: (value: string) => void;
  nameError?: string;
  /** Rendered ONLY when there is more than one eligible generation. */
  generationOptions: Array<{ value: string; label: string; disabled?: boolean }>;
  generationId: string;
  onGenerationChange: (id: string) => void;
  /**
   * Quick create (Task B7): pin the latest completed analysis automatically,
   * through the SAME `onAnalysisSelect` an operator pick travels on. The guided
   * wizard leaves it off — it always opens unpinned.
   */
  autoPinLatest?: boolean;
  /**
   * The analysis list's hydration state, forwarded from `InsightAnalysisSelect`
   * (W12 Task B9d). Quick create needs it to hold its readiness chip at
   * «Checking…» instead of counting blockers before the list has arrived.
   */
  onHydrationChange?: (state: InsightAnalysisHydration) => void;
  disabled?: boolean;
}

export const AnalysisStep: React.FC<AnalysisStepProps> = ({
  projectId,
  analysisId,
  onAnalysisSelect,
  acceptedVariableCounts,
  name,
  onNameChange,
  nameError,
  generationOptions,
  generationId,
  onGenerationChange,
  autoPinLatest = false,
  onHydrationChange,
  disabled = false,
}) => (
  <Stack spacing={3}>
    <InsightAnalysisSelect
      projectId={projectId}
      value={analysisId}
      onChange={onAnalysisSelect}
      acceptedVariableCounts={acceptedVariableCounts}
      autoPinLatest={autoPinLatest}
      onHydrationChange={onHydrationChange}
      disabled={disabled}
      data-testid={STRATEGY_LIST.insightAnalysisSelect}
    />

    <StyledTextField
      label="Name"
      value={name}
      onChange={onNameChange}
      error={Boolean(nameError)}
      errorMessage={nameError}
      helperText="Suggested from the analysis and the strategy type — rename freely."
      required
      fullWidth
      maxLength={100}
      autoFocus
      disabled={disabled}
      data-testid={STRATEGY_LIST.formName}
    />

    {generationOptions.length > 1 && (
      <StyledSelectDropdown
        label="Variable set"
        value={generationId}
        onChange={(value) => onGenerationChange(String(value))}
        options={generationOptions}
        fullWidth
        disabled={disabled}
        data-testid={STRATEGY_LIST.insightVariableGenerationSelect}
      />
    )}
  </Stack>
);

AnalysisStep.displayName = 'AnalysisStep';
